import { useState, useRef, useEffect } from 'react';
import { Box, IconButton, CircularProgress, Alert } from '@mui/material';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import homePlaceholder from '../assets/homeplaceholderimage.jpg';
import ImageViewer from './ImageViewer';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import { Attachment } from '../hooks/useHomeAttachments';
import { useChat } from '../hooks/useChat';

interface ImageSliderProps {
  homeId?: string; // Optional - can still override the context if needed
  images?: string[];
  height?: number;
  areaId?: string; // New prop to filter images by area
}

const ImageSlider = ({ homeId: homeIdProp, images, height = 100, areaId }: ImageSliderProps) => {
  // Add state for the image viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Use the context instead of the hook directly
  const { 
    attachments, 
    isLoading,
    error,
    getAttachmentUrl,
    isImageFile,
    homeId: contextHomeId,
    deleteAttachment
  } = useHomeAttachmentsContext();
  
  // Get areas data from chat context
  const { areas } = useChat();
  
  // Use the homeId from props if provided, otherwise use the one from context
  const effectiveHomeId = homeIdProp || contextHomeId;
  
  // If no images are provided, use placeholder images
  const demoImages = Array(3).fill(homePlaceholder);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  
  // Filter for only image attachments
  const imageAttachments = attachments.filter(att => isImageFile(att.fileType));
  
  // If areaId is provided, filter images by that area
  const filteredAttachments = areaId 
    ? imageAttachments.filter(att => {
        // Find the area with the matching id
        const area = areas.find(a => a.area_name === areaId);
        // Check if this attachment's id is in the area's image_ids array
        return area && area.image_ids.includes(String(att.key));
      })
    : imageAttachments;
  
  // Get image URLs from attachments or use provided images
  const imagesToRender = images?.length 
    ? images // If images are explicitly provided, use them
    : effectiveHomeId && filteredAttachments.length > 0
    ? filteredAttachments.map(att => getAttachmentUrl(att))
    : demoImages;
  
  // Check if we need to show the navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
        setShowRightArrow(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 10);
        setShowLeftArrow(scrollLeft > 10);
      }
    };
    
    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [attachments]);

  // Update arrow visibility when scrolling
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
      setShowRightArrow(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 10);
      setShowLeftArrow(scrollLeft > 10);
    }
  };
  
  // Scroll right when right arrow is clicked
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (clientWidth * 0.8),
        behavior: 'smooth'
      });
    }
  };
  
  // Scroll left when left arrow is clicked
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, currentScroll - (clientWidth * 0.8)),
        behavior: 'smooth'
      });
    }
  };

  // Handle image click to open the viewer
  const handleImageClick = (index: number) => {
    // Remove the unnecessary state update
    setSelectedImageIndex(index);
    setViewerOpen(true);
  };

  // Handle closing the viewer
  const handleCloseViewer = () => {
    setViewerOpen(false);
  };
  
  return (
    <Box sx={{ position: 'relative', width: '100%', my: 2 }}>
      {isLoading && effectiveHomeId ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, height: height }}>
          <CircularProgress size={24} />
        </Box>
      ) : error && effectiveHomeId ? (
        <Box sx={{ py: 1, height: height }}>
          <Alert 
            severity="error" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              height: '100%', 
              '& .MuiAlert-message': { 
                display: 'flex', 
                alignItems: 'center' 
              } 
            }}
          >
            {error}
          </Alert>
        </Box>
      ) : (
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            py: 1,
            px: 1,
            '&::-webkit-scrollbar': {
              display: 'none', // Hide scrollbar for Chrome/Safari
            },
            scrollbarWidth: 'none', // Hide scrollbar for Firefox
            msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
            gap: 2,
          }}
        >
          {imagesToRender.map((image, index) => (
            <Box
              key={index}
              sx={{
                minWidth: height * 1.4, // Width proportional to height
                height: height,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
                boxShadow: 1,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 3,
                }
              }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ))}
        </Box>
      )}
      
      {showLeftArrow && (
        <IconButton
          onClick={scrollLeft}
          disableRipple
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent !important',
            padding: 0,
            border: 'none',
            boxShadow: 'none',
            zIndex: 1,
            // Override all possible hover/focus/active states
            '&:hover, &:active, &:focus, &.Mui-focusVisible, &::before, &::after': {
              backgroundColor: 'transparent !important',
              outline: 'none',
            },
            // Style the SVG element
            '& .MuiSvgIcon-root': {
              filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))',
              transition: 'transform 0.2s',
            },
            '&:hover .MuiSvgIcon-root': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <ArrowCircleLeftIcon fontSize="large" color="primary" />
        </IconButton>
      )}
      
      {showRightArrow && (
        <IconButton
          onClick={scrollRight}
          disableRipple
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent !important',
            padding: 0,
            border: 'none',
            boxShadow: 'none',
            zIndex: 1,
            // Override all possible hover/focus/active states
            '&:hover, &:active, &:focus, &.Mui-focusVisible, &::before, &::after': {
              backgroundColor: 'transparent !important',
              outline: 'none',
            },
            // Style the SVG element
            '& .MuiSvgIcon-root': {
              filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))',
              transition: 'transform 0.2s',
            },
            '&:hover .MuiSvgIcon-root': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <ArrowCircleRightIcon fontSize="large" color="primary" />
        </IconButton>
      )}

      {/* Add the ImageViewer component */}
      <ImageViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        // Conditionally pass either images or attachments based on what's being displayed
        {...(images?.length
          ? { images: imagesToRender, initialIndex: selectedImageIndex }
          : { attachments: filteredAttachments, initialIndex: selectedImageIndex,
              onDelete: async (attachment: Attachment) => {
                const remaining = filteredAttachments.length - 1;
                const success = await deleteAttachment(attachment.key);
                return { success, remainingCount: remaining };
              }
            }
        )}
      />
    </Box>
  );
};

export default ImageSlider;